import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { CheckIcon, XIcon } from '@phosphor-icons/react';

import {
  useAdminBetaRequests,
  approveBetaRequest,
  denyBetaRequest,
  type BetaJoinRequest,
  type JoinRequestStatusFilter,
} from '@/api-models/admin/beta-requests';
import { formatDate } from '@/features/admin/format';
import { Pagination } from '@/ui/Pagination';
import { SpinnerBlock } from '@/ui/Spinner';
import { FilterPills } from '@/ui/FilterPills';
import { Button } from '@/ui/button/Button';
import { Modal } from '@/ui/Modal';
import { Badge, type BadgeProps } from '@/ui/Badge';
import { Card } from '@/ui/Card';
import { Textarea } from '@/ui/Textarea';
import { Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow } from '@/ui/Table';

const PAGE_SIZE = 25;

const STATUS_PILLS: { label: string; mode: JoinRequestStatusFilter }[] = [
  { label: 'Pending', mode: 'pending' },
  { label: 'Approved', mode: 'approved' },
  { label: 'Denied', mode: 'denied' },
  { label: 'All', mode: 'all' },
];

const STATUS_VARIANT: Record<BetaJoinRequest['status'], BadgeProps['variant']> = {
  pending: 'warning',
  approved: 'success',
  denied: 'danger',
};

const STATUS_LABELS: Record<BetaJoinRequest['status'], string> = {
  pending: 'Pending',
  approved: 'Approved',
  denied: 'Denied',
};

interface RequestRowProps {
  request: BetaJoinRequest;
  busy: boolean;
  onApprove: (req: BetaJoinRequest) => void;
  onDeny: (req: BetaJoinRequest) => void;
}

function RequestRow({ request, busy, onApprove, onDeny }: RequestRowProps) {
  const isPending = request.status === 'pending';

  return (
    <TableRow>
      <TableCell>
        <div className="min-w-0">
          <p className="truncate text-xs font-medium text-fg">{request.name}</p>
          <p className="truncate text-2xs text-fg-muted">{request.email}</p>
        </div>
      </TableCell>
      <TableCell className="max-w-40 truncate text-2xs">{request.job_title || '—'}</TableCell>
      <TableCell className="max-w-40 truncate text-2xs">{request.company || '—'}</TableCell>
      <TableCell className="max-w-44 truncate text-2xs text-fg-muted">{request.heard_from || '—'}</TableCell>
      <TableCell className="whitespace-nowrap text-2xs text-fg-muted tabular-nums">
        {formatDate(request.created_at)}
      </TableCell>
      <TableCell>
        <Badge variant={STATUS_VARIANT[request.status]}>{STATUS_LABELS[request.status]}</Badge>
      </TableCell>
      <TableCell className="text-right">
        {isPending ? (
          <div className="flex items-center justify-end gap-2">
            <Button
              size={7}
              variant="primary"
              onClick={() => onApprove(request)}
              disabled={busy}
              iconLeft={<CheckIcon size={12} />}
            >
              Approve
            </Button>
            <Button
              size={7}
              variant="secondary"
              onClick={() => onDeny(request)}
              disabled={busy}
              iconLeft={<XIcon size={12} />}
            >
              Deny
            </Button>
          </div>
        ) : (
          <span className="text-2xs text-fg-muted">
            {request.reviewed_at ? `Reviewed ${formatDate(request.reviewed_at)}` : ''}
          </span>
        )}
      </TableCell>
    </TableRow>
  );
}

export function AdminBetaRequestsPage() {
  const [statusFilter, setStatusFilter] = useState<JoinRequestStatusFilter>('pending');
  const [page, setPage] = useState(1);
  const [busyId, setBusyId] = useState<string | null>(null);

  const [approveTarget, setApproveTarget] = useState<BetaJoinRequest | null>(null);
  const [denyTarget, setDenyTarget] = useState<BetaJoinRequest | null>(null);
  const [denyReason, setDenyReason] = useState('');

  const query = useMemo(() => ({ status: statusFilter, page, pageSize: PAGE_SIZE }), [statusFilter, page]);
  const { requests, total, mutate, isLoading } = useAdminBetaRequests(query);

  function handleFilterChange(mode: JoinRequestStatusFilter) {
    setStatusFilter(mode);
    setPage(1);
  }

  async function confirmApprove() {
    if (!approveTarget) return;
    const id = approveTarget.id;
    setBusyId(id);
    try {
      await approveBetaRequest(id);
      await mutate();
      toast.success('Approved — invitation email sent');
      setApproveTarget(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to approve request');
    } finally {
      setBusyId(null);
    }
  }

  async function confirmDeny() {
    if (!denyTarget) return;
    const id = denyTarget.id;
    setBusyId(id);
    try {
      await denyBetaRequest(id, denyReason);
      await mutate();
      toast.success('Request denied');
      setDenyTarget(null);
      setDenyReason('');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to deny request');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <>
      <header>
        <h1 className="page-title">Beta join requests</h1>
        <p className="page-subtitle">
          People who submitted the join form on the sign-up page. Approving auto-creates an invitation and sends them
          the welcome email.
        </p>
      </header>

      <div className="flex flex-col gap-4">
        <FilterPills pills={STATUS_PILLS} activeMode={statusFilter} onChange={handleFilterChange} />

        <Card padding="none" radius="2xl" className="overflow-hidden">
          {isLoading && requests.length === 0 ? (
            <div className="py-12">
              <SpinnerBlock message="Loading requests…" />
            </div>
          ) : requests.length === 0 ? (
            <p className="p-8 text-center text-xs text-fg-muted">No requests match this filter.</p>
          ) : (
            <Table>
              <TableHead>
                <tr>
                  <TableHeaderCell>Person</TableHeaderCell>
                  <TableHeaderCell>Job title</TableHeaderCell>
                  <TableHeaderCell>Company</TableHeaderCell>
                  <TableHeaderCell>Heard from</TableHeaderCell>
                  <TableHeaderCell className="w-36">Requested</TableHeaderCell>
                  <TableHeaderCell className="w-28">Status</TableHeaderCell>
                  <TableHeaderCell className="w-55 text-right">Actions</TableHeaderCell>
                </tr>
              </TableHead>
              <TableBody>
                {requests.map((req) => (
                  <RequestRow
                    key={req.id}
                    request={req}
                    busy={busyId === req.id}
                    onApprove={setApproveTarget}
                    onDeny={setDenyTarget}
                  />
                ))}
              </TableBody>
            </Table>
          )}
        </Card>

        <Pagination page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} />
      </div>

      <Modal
        isOpen={approveTarget !== null}
        onClose={() => setApproveTarget(null)}
        title="Approve and invite"
        description={
          approveTarget
            ? `${approveTarget.name} (${approveTarget.email}) will get an invitation and welcome email immediately.`
            : ''
        }
      >
        <div className="px-6 pb-6 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setApproveTarget(null)} disabled={busyId !== null}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={confirmApprove}
            isLoading={busyId === approveTarget?.id}
            disabled={busyId !== null}
            iconLeft={<CheckIcon size={14} />}
          >
            Approve and send invite
          </Button>
        </div>
      </Modal>

      <Modal
        isOpen={denyTarget !== null}
        onClose={() => {
          setDenyTarget(null);
          setDenyReason('');
        }}
        title="Deny request"
        description={denyTarget ? `Deny ${denyTarget.name} (${denyTarget.email})?` : ''}
      >
        <div className="px-6 pb-6 flex flex-col gap-3">
          <label className="block text-2xs text-fg-muted">
            Reason (optional, internal — not emailed)
            <Textarea
              value={denyReason}
              onChange={(e) => setDenyReason(e.target.value)}
              rows={3}
              placeholder="Why are you denying this?"
              className="mt-1.5"
            />
          </label>
          <div className="flex justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() => {
                setDenyTarget(null);
                setDenyReason('');
              }}
              disabled={busyId !== null}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDeny}
              isLoading={busyId === denyTarget?.id}
              disabled={busyId !== null}
            >
              Deny request
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
